import { Link } from 'react-router-dom';
import HeroCarousel, { type HeroSlide } from '@/components/feature/HeroCarousel';
import ContentCard from '@/components/feature/ContentCard';
import SectionHeading from '@/components/base/SectionHeading';
import { useSEO } from '@/hooks/useSEO';
import { newsItems, videoItems, topicItems, formatViews } from '@/hooks/useContent';

const heroSlides: HeroSlide[] = [
  {
    id: 'news-01',
    title: '暑期档原创动画票房破纪录，小成本团队成最大黑马',
    summary: '没有大牌配音与超级宣发，一部原创动画靠口碑一路逆袭，成为今年暑期档讨论度最高的作品之一。',
    cover:
      'https://readdy.ai/api/search-image?query=stylized%20abstract%20cinema%20theme%20background%20with%20glowing%20orange%20light%20rays%20and%20film%20reel%20silhouettes%2C%20deep%20gray-blue%20dark%20tones%2C%20cinematic%20gradient%2C%20modern%20minimalist%2C%20no%20text&width=1600&height=900&seq=hero-main-01&orientation=landscape',
    tag: '今日焦点',
    author: '一起艹资讯组',
    publishedAt: '2026-10-12',
  },
  {
    id: 'news-02',
    title: '三大舞台阵容全曝光，今年秋季音乐节玩法升级',
    summary: '主舞台、电子舞台与民谣小舞台同步官宣，主办方还新增了露营区与深夜电台环节。',
    cover:
      'https://readdy.ai/api/search-image?query=stylized%20abstract%20music%20festival%20stage%20with%20warm%20orange%20spotlights%20and%20crowd%20silhouette%2C%20deep%20gray-blue%20dark%20night%20tones%2C%20cinematic%20gradient%2C%20modern%20minimalist%2C%20no%20text&width=1600&height=900&seq=hero-main-02&orientation=landscape',
    tag: '现场直击',
    author: '演出观察',
    publishedAt: '2026-10-11',
  },
  {
    id: 'video-01',
    title: '夏日音乐节高光三分钟，从日落唱到星空',
    summary: '把三天的现场浓缩成三分钟，完整保留观众的跟唱与欢呼，隔着屏幕也能感受那种被音乐包围的现场感。',
    cover:
      'https://readdy.ai/api/search-image?query=stylized%20abstract%20concert%20crowd%20with%20glowing%20phone%20lights%20at%20dusk%2C%20vibrant%20orange%20and%20deep%20gray-blue%20tones%2C%20cinematic%20gradient%2C%20modern%20minimalist%2C%20no%20text&width=1600&height=900&seq=hero-main-03&orientation=landscape',
    tag: '热门视频',
    author: '一起艹视频组',
    publishedAt: '2026-10-12',
  },
  {
    id: 'news-08',
    title: '虚拟偶像开唱：当技术遇到情感，观众买账了吗',
    summary: '全息投影、实时动捕、千人同屏互动，技术越来越成熟，但真正打动人心的仍然是情感连接。',
    cover:
      'https://readdy.ai/api/search-image?query=stylized%20abstract%20holographic%20virtual%20stage%20with%20neon%20orange%20particles%20and%20digital%20glow%2C%20deep%20gray-blue%20dark%20tones%2C%20cinematic%20gradient%2C%20modern%20minimalist%2C%20no%20text&width=1600&height=900&seq=hero-main-04&orientation=landscape',
    tag: '科技娱乐',
    author: '一起艹科技',
    publishedAt: '2026-10-05',
  },
];

const quickEntries = [
  {
    to: '/category/news',
    label: '娱乐资讯',
    desc: '最新热门资讯速览',
    icon: 'ri-newspaper-line',
    tone: 'bg-primary-500 text-background-50',
    count: `${newsItems.length} 篇更新`,
  },
  {
    to: '/category/video',
    label: '精选视频',
    desc: '影视综艺短视频合集',
    icon: 'ri-play-circle-line',
    tone: 'bg-accent-500 text-accent-950',
    count: `${videoItems.length} 条视频`,
  },
  {
    to: '/category/topic',
    label: '互动话题',
    desc: '一起聊聊你的看法',
    icon: 'ri-chat-3-line',
    tone: 'bg-secondary-700 text-background-50',
    count: `${topicItems.length} 个热帖`,
  },
  {
    to: '/special',
    label: '精选专题',
    desc: '成体系的内容策划',
    icon: 'ri-bookmark-3-line',
    tone: 'bg-primary-700 text-background-50',
    count: '每周更新',
  },
];

const hotTags = ['影视', '活动', '音乐', '短剧', '综艺', '虚拟偶像', '治愈', '片单'];

export default function Home() {
  useSEO(
    '一起艹 - 优质娱乐休闲内容聚合平台',
    '一起艹为您提供丰富多样的娱乐内容，包括最新热门资讯、视频和互动话题，是广大娱乐爱好者的交流与分享平台。',
    '一起艹,娱乐,休闲,视频,资讯,互动,分享,社区',
  );

  const featuredNews = newsItems[0];
  const newsList = newsItems.slice(1, 5);
  const hotVideos = videoItems.slice(0, 4);
  const hotTopics = topicItems.slice(0, 3);

  return (
    <div className="w-full">
      <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 pt-5 md:pt-8">
        <HeroCarousel slides={heroSlides} />

        {/* 分类快捷入口 */}
        <section className="mt-8 md:mt-10" aria-label="分类快捷入口">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5">
            {quickEntries.map((entry) => (
              <Link
                key={entry.to}
                to={entry.to}
                className="group p-4 md:p-5 rounded-lg border border-background-200 bg-background-50 hover:border-primary-300 cursor-pointer transition-colors"
              >
                <div className={`w-11 h-11 md:w-12 md:h-12 flex items-center justify-center rounded-lg ${entry.tone}`}>
                  <i className={`${entry.icon} text-xl md:text-2xl`} />
                </div>
                <h3 className="mt-3.5 font-heading text-lg md:text-xl text-foreground-950 group-hover:text-primary-600 transition-colors">
                  {entry.label}
                </h3>
                <p className="mt-1 text-xs md:text-sm text-foreground-500">{entry.desc}</p>
                <p className="mt-3 text-xs text-foreground-400 flex items-center gap-1">
                  {entry.count}
                  <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
                </p>
              </Link>
            ))}
          </div>
        </section>
      </div>

      {/* 最新资讯 */}
      <section className="mt-14 md:mt-20 bg-background-100 py-12 md:py-16" aria-label="最新资讯">
        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
          <SectionHeading
            title="最新娱乐资讯"
            subtitle="紧跟热点，第一时间掌握娱乐动态"
            action={
              <Link
                to="/category/news"
                className="inline-flex items-center gap-1 px-4 py-2 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 whitespace-nowrap cursor-pointer transition-colors"
              >
                查看全部
                <i className="ri-arrow-right-line" />
              </Link>
            }
          />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
            <div className="lg:col-span-5">
              <Link
                to={`/detail/${featuredNews.id}`}
                className="group block rounded-lg overflow-hidden border border-background-200 bg-background-50 hover:border-primary-300 h-full cursor-pointer transition-colors"
              >
                <div className="relative w-full aspect-[4/3] bg-background-200 overflow-hidden">
                  <img
                    src={featuredNews.cover}
                    alt={featuredNews.title}
                    title={`${featuredNews.title} - 一起艹`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <span className="absolute top-3 left-3 px-3 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-medium">
                    头条推荐
                  </span>
                </div>
                <div className="p-5">
                  <h3 className="font-heading text-xl md:text-2xl text-foreground-950 leading-snug group-hover:text-primary-600 transition-colors">
                    {featuredNews.title}
                  </h3>
                  <p className="mt-2.5 text-sm text-foreground-500 line-clamp-3">{featuredNews.summary}</p>
                  <div className="mt-4 flex items-center gap-4 text-xs text-foreground-400">
                    <span className="flex items-center gap-1">
                      <i className="ri-user-3-line" />
                      {featuredNews.author}
                    </span>
                    <span>{featuredNews.publishedAt}</span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line" />
                      {formatViews(featuredNews.views)}
                    </span>
                  </div>
                </div>
              </Link>
            </div>
            <div className="lg:col-span-7 flex flex-col gap-4">
              {newsList.map((item) => (
                <ContentCard key={item.id} item={item} variant="wide" />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 热门视频 */}
      <section className="mt-14 md:mt-20" aria-label="热门视频">
        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
          <SectionHeading
            title="热门视频"
            subtitle="精彩影视、综艺与短视频，一次看个够"
            action={
              <Link
                to="/category/video"
                className="inline-flex items-center gap-1 px-4 py-2 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 whitespace-nowrap cursor-pointer transition-colors"
              >
                更多视频
                <i className="ri-arrow-right-line" />
              </Link>
            }
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {hotVideos.map((item) => (
              <ContentCard key={item.id} item={item} />
            ))}
          </div>
        </div>
      </section>

      {/* 互动话题 */}
      <section className="mt-14 md:mt-20 bg-background-100 py-12 md:py-16" aria-label="互动话题">
        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
          <SectionHeading
            title="热议互动话题"
            subtitle="社区正在讨论的话题，加入一起聊聊"
            action={
              <Link
                to="/category/topic"
                className="inline-flex items-center gap-1 px-4 py-2 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 whitespace-nowrap cursor-pointer transition-colors"
              >
                进入社区
                <i className="ri-arrow-right-line" />
              </Link>
            }
          />
          <div className="flex flex-wrap gap-2.5 mb-6">
            {hotTags.map((tag) => (
              <Link
                key={tag}
                to={`/search?q=${encodeURIComponent(tag)}`}
                className="px-4 py-2 rounded-full bg-background-50 border border-background-200 text-sm text-foreground-700 hover:border-accent-400 hover:text-accent-800 cursor-pointer transition-colors"
              >
                # {tag}
              </Link>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
            {hotTopics.map((item) => (
              <Link
                key={item.id}
                to={`/detail/${item.id}`}
                className="group p-5 rounded-lg border border-background-200 bg-background-50 hover:border-primary-300 cursor-pointer transition-colors"
              >
                <div className="flex items-center justify-between">
                  <span className="px-2.5 py-0.5 rounded-full bg-secondary-100 text-secondary-800 text-xs font-medium">
                    话题
                  </span>
                  <span className="text-xs text-foreground-400 flex items-center gap-1">
                    <i className="ri-chat-smile-2-line" />
                    {formatViews(item.participants)} 人参与
                  </span>
                </div>
                <h3 className="mt-4 font-medium text-base text-foreground-950 line-clamp-2 group-hover:text-primary-600 transition-colors">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm text-foreground-500 line-clamp-2">{item.summary}</p>
                <p className="mt-4 text-xs text-foreground-400 flex items-center gap-1">
                  <i className="ri-time-line" />
                  最新回复 {item.lastReplyAt}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 网站简介 */}
      <section className="mt-14 md:mt-20" aria-label="平台简介">
        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
          <div className="relative overflow-hidden rounded-xl bg-secondary-900 px-6 py-12 md:px-14 md:py-16">
            <span className="absolute -right-10 -top-10 w-44 h-44 rounded-full bg-primary-500/20" />
            <span className="absolute right-24 bottom-0 w-28 h-28 rounded-full bg-accent-500/20" />
            <div className="relative max-w-3xl">
              <h2 className="font-heading text-2xl md:text-4xl text-background-50 leading-tight">
                一个属于娱乐爱好者的内容聚合站
              </h2>
              <p className="mt-4 text-sm md:text-base text-secondary-200 leading-relaxed">
                一起艹把娱乐新闻、精选视频与互动话题聚在一处：既能快速掌握最新动态，也能在一个个话题里
                找到同好。我们希望这里不只是一个浏览的地方，更是能参与、能讨论、能发现的娱乐社区。
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 whitespace-nowrap cursor-pointer transition-colors"
                >
                  了解我们
                  <i className="ri-arrow-right-line" />
                </Link>
                <Link
                  to="/special"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-secondary-600 text-secondary-100 text-sm font-medium hover:border-accent-400 hover:text-accent-400 whitespace-nowrap cursor-pointer transition-colors"
                >
                  逛精选专题
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}